import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import api from '../services/api';

const ADMIN_EMAIL_DOMAIN = 'consularcapital.com';

function isConsularAdminEmail(email: string | undefined) {
  return !!email?.toLowerCase().trim().endsWith(`@${ADMIN_EMAIL_DOMAIN}`);
}

export type AuthUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  entitlements: string[];
};

type AuthContextValue = {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAdmin: boolean;
  hasEntitlement: (entitlement: string) => boolean;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser({
        ...data.user,
        entitlements: data.user.entitlements || [],
      });
    } catch {
      localStorage.removeItem('authToken');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email: string, password: string) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('authToken', data.access_token);
    setUser({
      ...data.user,
      entitlements: data.user.entitlements || [],
    });
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
  };

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      logout,
      isAdmin:
        !!user?.roles?.includes('admin') && isConsularAdminEmail(user.email),
      hasEntitlement: (entitlement: string) => {
        if (!user) return false;
        if (
          user.roles?.includes('admin') &&
          isConsularAdminEmail(user.email)
        ) {
          return true;
        }
        return (user.entitlements || []).includes(entitlement);
      },
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside provider');
  return ctx;
}
